import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { resolveSeo, SEO_ORIGIN, OG_IMAGE } from "@/lib/seoConfig";

// Setzt einen <meta>-Eintrag (nach name ODER property); legt ihn bei Bedarf an.
function setMeta(kind, key, content) {
  const sel = `meta[${kind}="${key}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(kind, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!href) { if (el) el.remove(); return; }
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
  el.setAttribute("href", href);
}

function setJsonLd(enabled) {
  const id = "ld-json-voyalyo";
  const existing = document.getElementById(id);
  if (!enabled) { if (existing) existing.remove(); return; }
  const data = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": SEO_ORIGIN + "/#organization",
        name: "Voyalyo",
        url: SEO_ORIGIN + "/",
        logo: SEO_ORIGIN + "/android-chrome-512x512.png",
        sameAs: [
          "https://apps.apple.com/app/voyalyo/id6806280451",
          "https://play.google.com/store/apps/details?id=com.emergent.travelmatching.q1qjq1",
        ],
      },
      {
        "@type": "WebSite",
        "@id": SEO_ORIGIN + "/#website",
        name: "Voyalyo",
        url: SEO_ORIGIN + "/",
        inLanguage: "de-DE",
        publisher: { "@id": SEO_ORIGIN + "/#organization" },
      },
      {
        "@type": "SoftwareApplication",
        "@id": SEO_ORIGIN + "/#app",
        name: "Voyalyo",
        url: SEO_ORIGIN + "/",
        description:
          "Voyalyo verbindet Reiseplanung mit dem KI-Reiseplaner Lyo, Reiseideen und der Suche nach passenden Reisepartnern.",
        applicationCategory: "TravelApplication",
        operatingSystem: "Android, iOS, Web",
        publisher: { "@id": SEO_ORIGIN + "/#organization" },
        sameAs: [
          "https://apps.apple.com/app/voyalyo/id6806280451",
          "https://play.google.com/store/apps/details?id=com.emergent.travelmatching.q1qjq1",
        ],
      },
    ],
  };
  let el = existing;
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
}

// Zentrale, seitenspezifische Head-Verwaltung (Title/Description/Canonical/OG/Twitter/Robots/JSON-LD).
// Läuft innerhalb des Routers und reagiert auf jeden Routenwechsel. Keine SSR – Googlebot rendert JS.
export default function SeoManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    const seo = resolveSeo(pathname);
    const url = seo.canonical || SEO_ORIGIN + "/";

    document.title = seo.title;
    setMeta("name", "description", seo.description);
    setMeta("name", "robots", seo.indexable ? "index, follow" : "noindex, nofollow");
    setCanonical(seo.indexable ? seo.canonical : null);

    // Open Graph
    setMeta("property", "og:title", seo.title);
    setMeta("property", "og:description", seo.description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", "Voyalyo");
    setMeta("property", "og:image", OG_IMAGE);

    // Twitter / X
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", seo.title);
    setMeta("name", "twitter:description", seo.description);
    setMeta("name", "twitter:image", OG_IMAGE);

    // JSON-LD nur auf der Startseite
    setJsonLd(pathname === "/" || pathname === "");
  }, [pathname]);

  return null;
}
